import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  DragIndicator as DragIndicatorIcon,
  ExitToApp as ExitToAppIcon,
  PanoramaRounded as PanoramaRoundedIcon,
} from '@material-ui/icons';

import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { formatPrice } from '../../../shared/utils/format-price.util';
import { TableBodySkeleton } from '../../../shared/components/Tables/TableBodySkeleton.component';
import { ROWS_PER_PAGE_OPTIONS } from '../../../shared/constants/table.constants';
import { Product } from '../../types/product.type';
import { ADMIN_PRODUCTS_APP_PATHS, PRODUCT_IMAGE_PATH } from '../../constants/products-app-paths.constants';
import StatusChip from '../../../shared/components/Chips/StatusChip.component';
import { StockChip } from '../../../shared/components/Chips/StockChip.component';
import ProductSizeChip from '../Chips/ProductSizeChip.component';

interface ProductsTableHeader {
  key: string;
  label: string;
  ariaLabel?: string;
  minWidth: number;
  align: 'left' | 'right' | 'center';
}

interface ProductsTableProps {
  products: Product[];
  totalCount: number;
  page: number;
  rowsPerPage: number;
  isLoading: boolean;
  isDragEnabled: boolean;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onPositionsChange?: (products: { id: string; position: number }[]) => void;
  onToggleActive: (productId: string, isActive: boolean) => Promise<boolean>;
}

const BASE_HEADERS: ProductsTableHeader[] = [
  { key: 'actions', label: '', ariaLabel: 'Actions', minWidth: 50, align: 'left' },
  { key: 'image', label: '', minWidth: 50, align: 'center' },
  { key: 'name', label: 'Article', minWidth: 200, align: 'left' },
  { key: 'category', label: 'Rubrique', minWidth: 150, align: 'left' },
  { key: 'price', label: 'Prix', minWidth: 80, align: 'right' },
  { key: 'tvaRate', label: 'TVA', minWidth: 80, align: 'center' },
  { key: 'size', label: 'Taille', minWidth: 80, align: 'center' },
  { key: 'stock', label: 'Stock', minWidth: 80, align: 'center' as const },
  { key: 'active', label: 'Statut', minWidth: 70, align: 'center' },
];

function getHeaders(isDragEnabled: boolean): ProductsTableHeader[] {
  if (!isDragEnabled) return BASE_HEADERS;

  const actionsHeader: ProductsTableHeader = {
    key: 'actions',
    label: '',
    ariaLabel: 'Actions',
    minWidth: 80,
    align: 'center',
  };
  return [actionsHeader, ...BASE_HEADERS.filter((header) => header.key !== 'actions')];
}

const useStyles = makeStyles((theme) => ({
  secondaryLine: {
    display: 'block',
    fontSize: 12,
    color: '#757575',
  },
  positionChip: {
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    fontWeight: 600,
    fontSize: 12,
    borderRadius: 4,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'left',
    gap: '1rem',
  },
  drag: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.4rem',
  },
  statusChip: {
    cursor: 'pointer',
    '& > .MuiChip-root': {
      cursor: 'pointer',
    },
  },
}));

function ProductStatusCell({
  product,
  onToggleActive,
}: {
  product: Product;
  onToggleActive: (productId: string, isActive: boolean) => void;
}) {
  const classes = useStyles();

  if (product.archivedAt) {
    return (
      <TableCell align="center">
        <StatusChip isActive={product.isActive} archived />
      </TableCell>
    );
  }

  return (
    <TableCell align="center">
      <span className={classes.statusChip} onClick={() => onToggleActive(product.id, !product.isActive)}>
        <StatusChip isActive={product.isActive} />
      </span>
    </TableCell>
  );
}

function SortableProductRow({
  product,
  onToggleActive,
}: {
  product: Product;
  onToggleActive: (productId: string, isActive: boolean) => void;
}) {
  const classes = useStyles();
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: product.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <TableRow ref={setNodeRef} style={style} hover tabIndex={-1}>
      <TableCell align="left">
        <div className={classes.actions}>
          <div className={classes.drag}>
            <DragIndicatorIcon {...attributes} {...listeners} style={{ cursor: 'grab', opacity: 0.6 }} />
            <Chip className={classes.positionChip} size="small" label={product.position} />
          </div>
          <IconButton
            size="small"
            aria-label="voir"
            component={Link}
            to={ADMIN_PRODUCTS_APP_PATHS.PRODUCT_DETAILS(product.id)}
          >
            <ExitToAppIcon />
          </IconButton>
        </div>
      </TableCell>
      <TableCell align="center">
        {product.photo ? (
          <img src={PRODUCT_IMAGE_PATH(product.photo)} alt={product.name} width={40} height={40} />
        ) : (
          <PanoramaRoundedIcon style={{ fontSize: 40, color: '#ccc' }} />
        )}
      </TableCell>
      <TableCell align="left">
        {product.name}
        <span className={classes.secondaryLine}>Réf. {product.reference}</span>
      </TableCell>
      <TableCell align="left">
        {product.category.name}
        <span className={classes.secondaryLine}>{product.subCategory.name}</span>
      </TableCell>
      <TableCell align="right">{formatPrice(product.price)}</TableCell>
      <TableCell align="center">{product.tvaRate}</TableCell>
      <TableCell align="center">
        <ProductSizeChip size={product.size} />
      </TableCell>
      <TableCell align="center">
        <StockChip stock={product.stock} />
      </TableCell>
      <ProductStatusCell product={product} onToggleActive={onToggleActive} />
    </TableRow>
  );
}

function ProductsTable({
  products,
  totalCount,
  page,
  rowsPerPage,
  isLoading,
  isDragEnabled,
  onPageChange,
  onRowsPerPageChange,
  onPositionsChange,
  onToggleActive,
}: ProductsTableProps) {
  const classes = useStyles();
  const [localProducts, setLocalProducts] = useState(products);

  useEffect(() => {
    setLocalProducts(products);
  }, [products]);

  const handleToggleActive = async (productId: string, isActive: boolean) => {
    const success = await onToggleActive(productId, isActive);
    if (success) {
      setLocalProducts((previous) =>
        previous.map((product) => (product.id === productId ? { ...product, isActive } : product))
      );
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = localProducts.findIndex((product) => product.id === active.id);
    const newIndex = localProducts.findIndex((product) => product.id === over.id);

    const reordered = arrayMove(localProducts, oldIndex, newIndex);
    const withPositions = reordered.map((product, index) => ({
      ...product,
      position: page * rowsPerPage + index + 1,
    }));

    setLocalProducts(withPositions);
    onPositionsChange?.(withPositions.map((product) => ({ id: product.id, position: product.position })));
  };

  const headers = getHeaders(isDragEnabled);
  const columnCount = headers.length;

  return (
    <Paper>
      <TableContainer style={{ maxHeight: '75svh' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {headers.map((header) => (
                <TableCell
                  key={header.key}
                  aria-label={header.ariaLabel}
                  align={header.align}
                  style={{ minWidth: header.minWidth, fontWeight: 'bold' }}
                >
                  {header.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          {isLoading && <TableBodySkeleton rows={rowsPerPage} columns={columnCount} />}
          {!isLoading && (
            <TableBody>
              {localProducts.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columnCount} style={{ textAlign: 'center' }}>
                    Aucun produit
                  </TableCell>
                </TableRow>
              )}
              {isDragEnabled ? (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext
                    items={localProducts.map((product) => product.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {localProducts.map((product) => (
                      <SortableProductRow key={product.id} product={product} onToggleActive={handleToggleActive} />
                    ))}
                  </SortableContext>
                </DndContext>
              ) : (
                localProducts.map((product) => (
                  <TableRow key={product.id} hover tabIndex={-1}>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        aria-label="voir"
                        component={Link}
                        to={ADMIN_PRODUCTS_APP_PATHS.PRODUCT_DETAILS(product.id)}
                      >
                        <ExitToAppIcon />
                      </IconButton>
                    </TableCell>
                    <TableCell align="center">
                      {product.photo ? (
                        <img src={PRODUCT_IMAGE_PATH(product.photo)} alt={product.name} width={40} height={40} />
                      ) : (
                        <PanoramaRoundedIcon style={{ fontSize: 40, color: '#ccc' }} />
                      )}
                    </TableCell>
                    <TableCell align="left">
                      {product.name}
                      <span className={classes.secondaryLine}>Réf. {product.reference}</span>
                    </TableCell>
                    <TableCell align="left">
                      {product.category.name}
                      <span className={classes.secondaryLine}>{product.subCategory.name}</span>
                    </TableCell>
                    <TableCell align="right">{formatPrice(product.price)}</TableCell>
                    <TableCell align="center">{product.tvaRate}</TableCell>
                    <TableCell align="center">
                      <ProductSizeChip size={product.size} />
                    </TableCell>
                    <TableCell align="center">
                      <StockChip stock={product.stock} />
                    </TableCell>
                    <ProductStatusCell product={product} onToggleActive={handleToggleActive} />
                  </TableRow>
                ))
              )}
            </TableBody>
          )}
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(event, newPage) => onPageChange(newPage)}
        onRowsPerPageChange={(event) => onRowsPerPageChange(parseInt(event.target.value, 10))}
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
      />
    </Paper>
  );
}

export default ProductsTable;
